import { FC } from 'react';
import { PageHeader, Tabs, Descriptions, Skeleton, Menu } from 'antd';
import { HomeOutlined } from '@ant-design/icons';
import moment from 'moment';
import { DATE_TIME_FORMAT } from 'constants/date';
import { SendNotificationModal } from 'components/molecules/SendNotificationModal';
import { SendNotificationForm } from 'components/molecules/SendNotificationForm';
import ContextualMenu from 'components/atoms/ContextualMenu';
import { DeleteUserConfirmModal } from 'components/molecules/DeleteUserConfirmModal';
import { EditUserModal } from 'components/molecules/EditUserModal';
import TabAssets from './TabAssets';
import { useUser } from './resources/useUser';
import './styles.scss';
import LinkButton from 'components/atoms/Button/LinkButton';

const UserPage: FC = () => {
  const {
    user,
    currentUser,
    activeTab,
    userPageLiterals,
    sendNotificationFormLiterals,
    sendNotificationModalLiterals,
    deleteUserModalLiterals,
    editUserModalLiterals,
    form,
    userRoleList,
    currentClient,
    onGoBack,
    handleDeleteUser,
    onShowDeleteUserPop,
    onClickSendNotification,
    onCancelSendNotification,
    onSendNotification,
    onChangeTab,
    onClickEditUser,
    onCloseEditUser,
    onEditUser,
    onClickClientLink,
    clientList,
    loading,
    isSendNotificationModalVisible,
    isSendingNotification,
    showingPop,
    deletingUser,
    isEditUserModalVisible,
    isCurrentUserSuperAdmin,
    editingUser,
  } = useUser();

  const {
    userLabel,
    unknown,
    deleteUser,
    sendNotification,
    assets,
    email,
    userRoles,
    roles,
    status,
    userStatus,
    createdAt,
    confirmedAt,
    notReported,
    editUserButtonText,
    clientLabel,
  } = userPageLiterals;

  return (
    <>
      <PageHeader
        ghost={false}
        title={`${userLabel}: ${user?.username.toLowerCase() !== 'unknown' ? user?.username : unknown}`}
        onBack={onGoBack}
        className='UserPage__header'
        extra={[
          <ContextualMenu placement={'bottomRight'} menu={
            <Menu>
              <Menu.Item key={'edit'} onClick={onClickEditUser}>
                {editUserButtonText}
              </Menu.Item>
              <Menu.Item key={'sendNotification'} onClick={onClickSendNotification} disabled={user?.id === currentUser}>
                {sendNotification}
              </Menu.Item>
              <Menu.Item
                key={'delete'}
                onClick={() => onShowDeleteUserPop(true)}
                disabled={user?.id === currentUser}
                danger
              >
                {deleteUser}
              </Menu.Item>
            </Menu>
          } />
        ]}
        footer={
          <Tabs defaultActiveKey='TabAsset' onChange={onChangeTab}>
            <Tabs.TabPane
              tab={
                <span>
                  <HomeOutlined /> {assets}
                </span>
              }
              key='assets'
            />
          </Tabs>
        }
      >
        {loading ? (
          <Skeleton active />
        ) : (
          <Descriptions column={3} size='small'>
            <Descriptions.Item key='username' label={<b>{userLabel}</b>}>
              {user?.username && user.username.toLowerCase() !== 'unknown' ? user.username : unknown}
            </Descriptions.Item>
            <Descriptions.Item key='email' label={<b>{email}</b>}>
              {user?.email || notReported}
            </Descriptions.Item>
            <Descriptions.Item key='roles' label={<b>{roles}</b>}>
              {userRoles}
            </Descriptions.Item>
            <Descriptions.Item key='status' label={<b>{status}</b>}>
              {user?.status ? userStatus : notReported}
            </Descriptions.Item>
            <Descriptions.Item key='created_at' label={<b>{createdAt}</b>}>
              {user?.created_at ? moment.unix(user.created_at).format(DATE_TIME_FORMAT) : notReported}
            </Descriptions.Item>
            <Descriptions.Item key='confirmed_at' label={<b>{confirmedAt}</b>}>
              {user?.confirmed_at ? moment.unix(user.confirmed_at).format(DATE_TIME_FORMAT) : notReported}
            </Descriptions.Item>
            <Descriptions.Item key='currentClient' label={<b>{clientLabel}</b>}>
              {!!currentClient
                ? (<LinkButton className={'UserPage__link'} title={currentClient} onClick={() => !!user && onClickClientLink(user.client.client_id)} />)
                : notReported
              }
            </Descriptions.Item>
          </Descriptions>
        )}
      </PageHeader>
      <div className='UserPage__tab'>{activeTab === 'assets' && <TabAssets key='assets' />}</div>
      <SendNotificationModal
        formId='sendNotification'
        literals={sendNotificationModalLiterals}
        onCancel={onCancelSendNotification}
        visible={isSendNotificationModalVisible}
        loading={isSendingNotification}
      >
        <SendNotificationForm
          formId='sendNotification'
          literals={sendNotificationFormLiterals}
          onSave={onSendNotification}
        />
      </SendNotificationModal>
      <DeleteUserConfirmModal
        literals={deleteUserModalLiterals}
        loading={deletingUser}
        open={showingPop}
        onCancel={() => onShowDeleteUserPop(false)}
        onConfirmDeleteUser={() => handleDeleteUser(user!)}
      />
      <EditUserModal
        form={form}
        literals={editUserModalLiterals}
        clientList={clientList}
        loading={editingUser}
        onFinish={onEditUser}
        userRoleList={userRoleList}
        onCancel={onCloseEditUser}
        open={isEditUserModalVisible}
        isRoleVisible={isCurrentUserSuperAdmin}
      />
    </>
  );
};

export default UserPage;
